import { useEffect, useId, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  deleteMidpointMediaJobApplication,
  fetchMidpointMediaCrmLead,
  fetchMidpointMediaJobApplications,
  patchMidpointMediaCrmLead,
} from "../../../api/midpointMediaAdminApi";
import { resolveClientPortalAssetUrl } from "../../../api/clientUploadApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import { formatClientDateTime } from "../../../utils/clientPortalFormat";
import "../../../styles/midpointMediaAdmin.css";

const APPLICATION_STATUSES = [
  "new",
  "contacted",
  "qualified",
  "nurturing",
  "converted",
  "closed",
];

function isPdf(resume) {
  if (!resume) return false;
  if (String(resume.mimeType || "").toLowerCase().includes("pdf")) return true;
  return /\.pdf$/i.test(String(resume.fileName || resume.fileUrl || ""));
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function useEscapeClose(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

function ResumeModal({ application, onClose }) {
  const titleId = useId();
  const resume = application?.resume;
  const url = resume?.fileUrl ? resolveClientPortalAssetUrl(resume.fileUrl) : "";
  const pdf = isPdf(resume);
  useEscapeClose(Boolean(application), onClose);

  if (!application) return null;

  return (
    <div className="mm-admin-modal-root" role="presentation" onClick={onClose}>
      <div
        className="mm-admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mm-admin-modal__header">
          <div>
            <p className="mm-admin-kicker">Résumé</p>
            <h2 id={titleId}>{application.name}</h2>
            <p className="muted">
              {resume?.fileName || "Attached résumé"}
              {application.roleType ? ` · ${application.roleType}` : ""}
            </p>
          </div>
          <div className="mm-admin-actions">
            {url ? (
              <a className="btn btn-secondary" href={url} target="_blank" rel="noreferrer">
                Open in new tab
              </a>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className="mm-admin-modal__body">
          {!url ? (
            <p className="empty-state">No résumé file is attached to this application.</p>
          ) : pdf ? (
            <iframe title={`Résumé for ${application.name}`} src={url} className="mm-admin-modal__frame" />
          ) : (
            <div className="mm-admin-card">
              <p className="mm-admin-lead">
                This résumé isn’t a PDF preview. Open it in a new tab to download or view the
                original file.
              </p>
              <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer">
                Download {resume.fileName || "résumé"}
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ApplicationDetailModal({
  application,
  canWrite,
  saving,
  errorMessage,
  viewBase,
  onClose,
  onSaveNotes,
  onOpenResume,
  onStatusChange,
}) {
  const titleId = useId();
  const [notes, setNotes] = useState(application?.internalNotes || "");
  useEscapeClose(Boolean(application), onClose);

  useEffect(() => {
    setNotes(application?.internalNotes || "");
  }, [application?.id, application?.internalNotes]);

  if (!application) return null;

  const form = application.form || {};
  const fieldGroups = [
    {
      title: "Contact",
      fields: [
        ["Full name", application.name],
        ["Email", form.email || application.email],
        ["Phone / WhatsApp", form.phone || application.phone],
        ["City", form.city || application.city],
        ["Preferred channel", form.preferredChannel || application.preferredChannel],
      ],
    },
    {
      title: "Role & qualifications",
      fields: [
        ["Role applied for", form.roleLabel || application.roleType],
        ["Spoken English", form.englishLevel || application.englishLevel],
        ["Relevant experience", form.relevantExperience || application.relevantExperience],
        ["Résumé file", form.resumeFileName || application.resumeFileName],
      ],
    },
    {
      title: "Interview availability",
      fields: [
        ["Preferred days", form.interviewDays || application.interviewDays],
        ["Preferred windows", form.interviewWindows || application.interviewWindows],
        ["Availability notes", form.availabilityNotes || application.availabilityNotes],
      ],
    },
    {
      title: "Submission details",
      fields: [
        ["Applied", formatClientDateTime(application.createdAt)],
        ["Reference", application.referenceId],
        ["Capture page", form.capturePage || application.capturePage],
        ["Source", form.source || application.source],
        ["Source component", form.sourceComponent || application.sourceComponent],
      ],
    },
  ];

  const dirty = notes !== (application.internalNotes || "");

  return (
    <div className="mm-admin-modal-root" role="presentation" onClick={onClose}>
      <div
        className="mm-admin-modal mm-admin-modal--detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mm-admin-modal__header">
          <div>
            <p className="mm-admin-kicker">Application</p>
            <h2 id={titleId}>{application.name}</h2>
            <p className="muted">
              {application.referenceId ? `${application.referenceId} · ` : ""}
              {application.roleType || "Job application"}
            </p>
          </div>
          <div className="mm-admin-actions">
            {application.opportunityId && viewBase ? (
              <Link
                className="btn btn-primary"
                to={`${viewBase}/opportunities/${application.opportunityId}`}
              >
                Work in pipeline
              </Link>
            ) : null}
            {application.resume?.fileUrl ? (
              <button type="button" className="btn btn-secondary" onClick={onOpenResume}>
                View résumé
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </header>

        <div className="mm-admin-modal__body mm-admin-modal__body--scroll">
          <div className="mm-admin-detail-grid">
            <section className="mm-admin-card">
              <h3>Submitted form</h3>
              <div className="mm-admin-fact-groups">
                {fieldGroups.map((group) => (
                  <div key={group.title} className="mm-admin-fact-group">
                    <h4>{group.title}</h4>
                    <dl className="mm-admin-facts">
                      {group.fields.map(([label, value]) => (
                        <div key={label} className="mm-admin-facts__row">
                          <dt>{label}</dt>
                          <dd>{displayValue(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
              {application.message ? (
                <div className="mm-admin-fact-group">
                  <h4>Full application summary</h4>
                  <pre className="mm-admin-pre">{application.message}</pre>
                </div>
              ) : null}
            </section>

            <section className="mm-admin-card">
              <h3>Hiring controls</h3>
              <label className="mm-admin-field">
                Status
                <select
                  value={application.status}
                  disabled={!canWrite || saving}
                  onChange={(event) => onStatusChange(application, event.target.value)}
                >
                  {APPLICATION_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>

              <label className="mm-admin-field">
                Internal notes
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={10}
                  maxLength={8000}
                  disabled={!canWrite || saving}
                  placeholder="Interview feedback, follow-ups, decision notes…"
                />
              </label>

              {errorMessage ? <p className="mm-admin-inline-error">{errorMessage}</p> : null}

              <div className="mm-admin-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canWrite || saving || !dirty}
                  onClick={() => onSaveNotes(application, notes)}
                >
                  {saving ? "Saving…" : "Save notes"}
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MidpointMediaAdminPeopleApplicationsPage() {
  const { clientViewId, paths, permissions, viewBase } = useMidpointMediaAdminView();
  const canWrite = Boolean(permissions?.viewOpportunities);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [q, setQ] = useState("");
  const [state, setState] = useState({ loading: true, error: "", rows: [] });
  const [busyId, setBusyId] = useState("");
  const [resumeApplication, setResumeApplication] = useState(null);
  const [detailApplication, setDetailApplication] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [detailSaving, setDetailSaving] = useState(false);

  const mergeApplication = (lead) => {
    setState((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.id === lead.id
          ? {
              ...row,
              ...lead,
              resume: lead.resume || row.resume,
              form: lead.form || row.form,
            }
          : row
      ),
    }));
    setDetailApplication((current) =>
      current?.id === lead.id
        ? {
            ...current,
            ...lead,
            resume: lead.resume || current.resume,
            form: lead.form || current.form,
          }
        : current
    );
  };

  const load = () => {
    if (!clientViewId) return Promise.resolve();
    setState((current) => ({ ...current, loading: true, error: "" }));
    return fetchMidpointMediaJobApplications(clientViewId, {
      q,
      status: statusFilter || undefined,
      limit: 100,
    })
      .then((body) => {
        setState({ loading: false, error: "", rows: body.rows || [] });
      })
      .catch((error) => {
        setState({ loading: false, error: error.message, rows: [] });
      });
  };

  useEffect(() => {
    if (!clientViewId) return undefined;
    let cancelled = false;
    load().finally(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on filter/search only
  }, [clientViewId, q, statusFilter]);

  const statusOptions = useMemo(() => APPLICATION_STATUSES, []);

  const openApplication = async (application) => {
    setDetailError("");
    setDetailApplication(application);
    if (!clientViewId) return;
    try {
      const body = await fetchMidpointMediaCrmLead(clientViewId, application.id);
      if (body.lead) {
        mergeApplication({ ...application, ...body.lead, resume: body.lead.resume || application.resume });
      }
    } catch (error) {
      setDetailError(error.message || "Unable to refresh application details.");
    }
  };

  const onStatusChange = async (application, status) => {
    if (!canWrite || status === application.status) return;
    setBusyId(application.id);
    setDetailSaving(true);
    setState((current) => ({ ...current, error: "" }));
    setDetailError("");
    try {
      const body = await patchMidpointMediaCrmLead(clientViewId, application.id, { status });
      mergeApplication({ ...application, ...body.lead, resume: application.resume });
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
      setDetailError(error.message);
    } finally {
      setBusyId("");
      setDetailSaving(false);
    }
  };

  const onSaveNotes = async (application, internalNotes) => {
    if (!canWrite) return;
    setDetailSaving(true);
    setDetailError("");
    try {
      const body = await patchMidpointMediaCrmLead(clientViewId, application.id, {
        internalNotes,
      });
      mergeApplication({ ...application, ...body.lead, resume: application.resume });
    } catch (error) {
      setDetailError(error.message || "Unable to save notes.");
    } finally {
      setDetailSaving(false);
    }
  };

  const onDelete = async (application) => {
    if (!canWrite) return;
    const ok = window.confirm(
      `Delete application for ${application.name}? This removes it from the hiring queue.`
    );
    if (!ok) return;
    setBusyId(application.id);
    setState((current) => ({ ...current, error: "" }));
    try {
      await deleteMidpointMediaJobApplication(clientViewId, application.id);
      setState((current) => ({
        ...current,
        rows: current.rows.filter((row) => row.id !== application.id),
      }));
      if (resumeApplication?.id === application.id) setResumeApplication(null);
      if (detailApplication?.id === application.id) setDetailApplication(null);
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setBusyId("");
    }
  };

  if (!clientViewId) return <p className="empty-state">No workspace assigned.</p>;

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">
          <Link to={`${paths.base}/people`}>People</Link> · Applications
        </p>
        <h1>Job applications</h1>
        <p className="mm-admin-lead">
          Venezuela careers submissions from{" "}
          <code>/v/midpoint-media/careers/venezuela</code>. Every application is a CRM lead you can
          work in the pipeline. Open an applicant to review the form answers and keep hiring notes.
        </p>
      </header>

      <div className="mm-admin-toolbar">
        <label>
          Search
          <input
            type="search"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Name, email, reference…"
          />
        </label>
        <label>
          Status
          <select
            value={statusFilter}
            onChange={(event) => {
              const next = event.target.value;
              const params = new URLSearchParams(searchParams);
              if (next) params.set("status", next);
              else params.delete("status");
              setSearchParams(params);
            }}
          >
            <option value="">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.loading ? <ClientPortalLoading message="Loading applications…" /> : null}
      {state.error ? <ClientPortalError message={state.error} /> : null}
      {!state.loading && !state.rows.length ? (
        <p className="empty-state">No matching applications.</p>
      ) : null}

      {state.rows.length ? (
        <div className="mm-admin-card" style={{ padding: 0, overflow: "auto" }}>
          <table className="mm-admin-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Role</th>
                <th>City / channel</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Résumé</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((application) => (
                <tr key={application.id}>
                  <td>
                    <button
                      type="button"
                      className="mm-admin-link-button"
                      onClick={() => openApplication(application)}
                    >
                      {application.name}
                    </button>
                    <div className="muted">{application.email || application.phone || "—"}</div>
                  </td>
                  <td>{application.roleType || application.leadType || "—"}</td>
                  <td>
                    <div>{application.city || "—"}</div>
                    <div className="muted">
                      {application.preferredChannel ||
                        application.preferredContactMethod ||
                        "—"}
                    </div>
                  </td>
                  <td>
                    <select
                      className="mm-admin-inline-select"
                      value={application.status}
                      disabled={busyId === application.id || !canWrite}
                      onChange={(event) => onStatusChange(application, event.target.value)}
                      aria-label={`Status for ${application.name}`}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{formatClientDateTime(application.createdAt)}</td>
                  <td>
                    {application.resume?.fileUrl ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setResumeApplication(application)}
                      >
                        View résumé
                      </button>
                    ) : (
                      <span className="muted">{application.resumeFileName || "No file"}</span>
                    )}
                  </td>
                  <td>
                    <div className="mm-admin-actions">
                      {application.opportunityId && viewBase ? (
                        <Link
                          className="btn btn-secondary"
                          to={`${viewBase}/opportunities/${application.opportunityId}`}
                        >
                          Work
                        </Link>
                      ) : (
                        <Link
                          className="btn btn-secondary"
                          to={`${paths.base}/crm/leads/${application.id}`}
                        >
                          Open lead
                        </Link>
                      )}
                      <button
                        type="button"
                        className="btn btn-danger-ghost"
                        disabled={busyId === application.id || !canWrite}
                        onClick={() => onDelete(application)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ApplicationDetailModal
        application={detailApplication}
        canWrite={canWrite}
        saving={detailSaving}
        errorMessage={detailError}
        viewBase={viewBase}
        onClose={() => {
          setDetailApplication(null);
          setDetailError("");
        }}
        onSaveNotes={onSaveNotes}
        onOpenResume={() => {
          if (detailApplication) setResumeApplication(detailApplication);
        }}
        onStatusChange={onStatusChange}
      />

      <ResumeModal
        application={resumeApplication}
        onClose={() => setResumeApplication(null)}
      />
    </div>
  );
}
